import { observer } from 'mobx-react';

import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';
import { DatePicker } from '@components/datePickers';
import { ConfirmPopup } from '@components/Popup';
import { SelectionButtons, SelectionButtonsModifiers } from '@components/SelectionButtons';
import { CreateInput } from './CreateInput';

import style from './CreateEhrForm.module.scss';

import { CreatePatientAttributes } from 'chaika-test-attributes';

import { store } from '@store';
import { translate } from '@chaika/i18n/helper';
import { useCreateEhrForm } from './hooks/useCreateEhrForm';
import { profileForm } from './profileForm';

interface Props {
    redirectOnCreate?: boolean;
    onCreate?(id?: string): void;
    onCancelCreate(): void;
}

export const CreateEhrForm = observer(({ redirectOnCreate, onCreate, onCancelCreate }: Props) => {
    const { authUser, legalEntityList } = store;

    const {
        isEhrExist,
        onSexChange,
        onBirthDateChange,
        onSaveButtonClick,
        onCancelButtonClick,
        onUseExistingEhrClick,
        onExistingEhrCloseButtonClick,
    } = useCreateEhrForm({ redirectOnCreate, onCreate, onCancelCreate });

    const legalEntity = authUser?.legalEntityId ? legalEntityList.get(authUser.legalEntityId) : undefined;
    const birthDate = profileForm.getDateBirthDate();
    const sex = profileForm.getFieldValue<string>('sex');

    const options = [
        {
            value: 'm',
            label: translate('maleShort'),
        },
        {
            value: 'f',
            label: translate('femaleShort'),
        },
    ];

    return (
        <div className={style.root}>
            {isEhrExist && (
                <ConfirmPopup
                    dataTest={CreatePatientAttributes.Confirmation}
                    title={`${translate('sameEhrAlreadeExist')}.`}
                    confirmText={translate('useExisingFem')}
                    cancelText={translate('cancel')}
                    onConfirm={onUseExistingEhrClick}
                    onCancel={onExistingEhrCloseButtonClick}
                />
            )}
            <div className={style.title}>{translate('creatingEhr')}</div>
            <div className={style.column}>
                <div className={style.inputWrapper}>
                    <CreateInput
                        name={'lastName'}
                        title={`${translate('lastName')}*`}
                    />
                </div>
                <div className={style.inputWrapper}>
                    <CreateInput
                        name={'firstName'}
                        title={`${translate('name')}*`}
                    />
                </div>
                <div className={style.inputWrapper}>
                    <CreateInput
                        name={'patronymic'}
                        title={translate('patronymic')}
                    />
                </div>
                <div className={style.doubleColumnBlock}>
                    <div className={style.shortInputWrapper}>
                        <DatePicker
                            dataTest={CreatePatientAttributes.Age}
                            title={`${translate('birthDate')}*`}
                            placeholderText={translate('birthDate')}
                            selected={birthDate}
                            maxDate={new Date()}
                            minDate={new Date('01/01/1900')}
                            onChange={onBirthDateChange}
                        />
                    </div>
                    <div className={style.shortInputWrapper}>
                        <div className={style.ageInputWrapper}>
                            <CreateInput
                                name={'age'}
                                title={translate('age')}
                                disabled={true}
                            />
                        </div>
                        <div className={style.sexSelectWrapper}>
                            <SelectionButtons
                                dataTest={CreatePatientAttributes.Sex}
                                selectedValue={sex}
                                values={options}
                                modifiers={[SelectionButtonsModifiers.FullHeight]}
                                onSelect={onSexChange}
                            />
                        </div>
                    </div>
                </div>
                <div className={style.shortInputWrapper}>
                    <CreateInput
                        country={legalEntity?.country}
                        name={'phone'}
                        type={'tel'}
                        title={`${translate('phone')}*`}
                    />
                </div>
            </div>
            <div className={style.buttonsBlockWrapper}>
                <div className={style.buttonWrapper}>
                    <Button
                        dataTest={CreatePatientAttributes.CreateButton}
                        disabled={!profileForm.isFormValid}
                        theme={ButtonTheme.Primary}
                        modifiers={[ButtonModifiers.Big, ButtonModifiers.InheritWidth]}
                        onClick={onSaveButtonClick}
                    >
                        {translate('create')}
                    </Button>
                </div>
                <div className={style.buttonWrapper}>
                    <Button
                        dataTest={CreatePatientAttributes.CancelButton}
                        theme={ButtonTheme.Light}
                        modifiers={[ButtonModifiers.Big, ButtonModifiers.InheritWidth]}
                        onClick={onCancelButtonClick}
                    >
                        {translate('cancel')}
                    </Button>
                </div>
            </div>
        </div>
    );
});
